import { useApp } from "../context/AppContext";
import { colors, inputStyle, PRICING, btnPrimary, btnSecondary, cardStyle, containerStyle, CONFIG } from "../data/config";
import Notification from "../components/Notification";

export default function Landing() {
  const {
    authUser, savedResults, paidTests, countdown,
    discountCode, setDiscountCode, discountApplied,
    setScreen, setCurrentTest, setAiReport, setAiReportFull, setResults,
    startTest, applyDiscount, getPrice,
  } = useApp();

  const journeySteps = [
    { key: "personality", step: 1, icon: "🪞", title: "My Personality", questions: 20, desc: "How your mind is wired - the way you think, decide, and show up in every room you walk into." },
    { key: "core", step: 2, icon: "🧬", title: "My Core", questions: 25, desc: "The Big Five traits science uses to predict your career performance, relationships, and wellbeing." },
    { key: "strengths", step: 3, icon: "⚡", title: "My Strengths", questions: 40, desc: "Your natural superpowers across 8 dimensions - and how to build your career around what you do best." },
    { key: "shadow", step: 4, icon: "🌑", title: "My Shadow", questions: 40, desc: "The hidden patterns quietly running your life - and how to integrate them so they stop costing you." },
    { key: "values", step: 5, icon: "🧭", title: "My Values & Drivers", questions: 40, desc: "What truly drives you at your core - and how to align your life and career with it." },
  ];

  return (
    <div style={containerStyle}>
      <Notification />

      {/* Sticky Nav */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(7,29,82,0.95)", backdropFilter: "blur(12px)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ fontSize: 17, fontWeight: 700, fontStyle: "italic", color: "#fff", letterSpacing: 0.5 }}>Mirror Mirror</div>
        {!authUser ? (
          <button onClick={() => setScreen("google-signin")} style={{ fontSize: 13, color: "#fff", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 20, padding: "6px 16px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
            Sign In
          </button>
        ) : (
          <button onClick={() => setScreen("your-journey")} style={{ fontSize: 13, color: "#fff", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 20, padding: "6px 14px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
            {authUser.photoURL && <img src={authUser.photoURL} alt="" style={{ width: 18, height: 18, borderRadius: "50%" }} />}
            My Journey
          </button>
        )}
      </div>

      {/* Header */}
      <div style={{ padding: "40px 24px 28px", textAlign: "center", position: "relative" }}>
        <div style={{ fontSize: 13, letterSpacing: 6, textTransform: "uppercase", color: "rgba(255,255,255,0.85)", marginBottom: 12, fontWeight: 700 }}>Scientific Self-Reflection</div>
        <h1 style={{ fontSize: 52, fontWeight: 300, lineHeight: 1.05, color: "#fff", marginBottom: 8, textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}>
          Mirror<br /><span style={{ fontWeight: 700, fontStyle: "italic" }}>Mirror</span>
        </h1>
        <div style={{ width: 36, height: 2.5, background: colors.gold, margin: "16px auto", boxShadow: "0 0 10px rgba(255,215,0,0.5)" }} />
      </div>

      {/* Hero copy */}
      <div style={{ padding: "0 24px 24px" }}>
        <p style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.35, marginBottom: 16, color: "#fff", textShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>
          You think you know yourself.<br />You don't. Not yet.
        </p>
        <p style={{ fontSize: 20, lineHeight: 1.7, color: "#fff", marginBottom: 16, fontWeight: 600 }}>
          Five assessments. Decades of psychology. One brutally honest mirror.
        </p>
        <p style={{ fontSize: 18, lineHeight: 1.8, color: "rgba(255,255,255,0.95)", marginBottom: 20, fontWeight: 400 }}>
          Each step in the journey reveals a different layer of who you are - your personality type, your science-backed trait profile, your strengths, your shadow, and what truly drives you. All grounded in research. All personalised to you.
        </p>
        <p style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.55, color: colors.gold }}>
          When you truly know yourself, everything changes - your career, your relationships, your life.
        </p>

        {!authUser && (
          <button style={{ ...btnPrimary, marginTop: 24, fontSize: 17, padding: "16px 28px" }} onClick={() => startTest("personality")}>
            Start Free - Takes 5 Minutes →
          </button>
        )}

        {/* Social proof */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 20, fontSize: 14, color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>
          <span>⭐⭐⭐⭐⭐</span>
          <span>500+ people have discovered themselves</span>
        </div>

        {/* Welcome back card for returning users */}
        {authUser && (
          <div style={{ ...cardStyle, background: "rgba(255,255,255,0.97)", marginTop: 20, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: colors.textLight, marginBottom: 4 }}>Welcome back, {authUser.displayName?.split(" ")[0]} 👋</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: colors.bgDark, marginBottom: 12 }}>Pick up where you left off</div>
            <button style={{ ...btnPrimary, fontSize: 14 }} onClick={() => setScreen("your-journey")}>
              View Your Journey →
            </button>
          </div>
        )}
      </div>

      {/* Testimonials */}
      <div style={{ margin: "0 24px 28px" }}>
        <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.55)", textAlign: "center", marginBottom: 14, fontWeight: 700 }}>What People Are Saying</div>
        {[
          { quote: "The Shadow report made me realise why I keep self-sabotaging. I've read it three times.", name: "Priya M., Product Manager" },
          { quote: "More accurate than any assessment I've taken. The Strengths section changed how I think about my career.", name: "Arjun S., MBA Student" },
          { quote: "Scary how well it knew me. The Values report is something I'll come back to for years.", name: "Anika R., Consultant" },
        ].map((t, i) => (
          <div key={i} style={{ ...cardStyle, marginBottom: 10 }}>
            <div style={{ fontSize: 15, color: "#091a2a", lineHeight: 1.7, fontStyle: "italic", marginBottom: 8 }}>"{t.quote}"</div>
            <div style={{ fontSize: 13, color: colors.textLight, fontWeight: 600 }}> -  {t.name}</div>
          </div>
        ))}
      </div>

      {/* Countdown */}
      <div style={{ margin: "0 24px 28px", padding: "18px 20px", background: "rgba(12,48,120,0.88)", borderRadius: 16, textAlign: "center", backdropFilter: "blur(12px)", border: "1px solid rgba(255,215,0,0.2)" }}>
        <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.85)", marginBottom: 10, fontWeight: 700 }}>Launch Pricing Ends In</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          {[["days", countdown.days], ["hrs", countdown.hours], ["min", countdown.mins], ["sec", countdown.secs]].map(([label, val]) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 30, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{String(val).padStart(2, "0")}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 14, color: colors.gold, marginTop: 12, fontWeight: 700 }}>Free previews + reports from ₹399</div>
        <div style={{ fontSize: 14, color: colors.gold, marginTop: 4, fontWeight: 600 }}>The mirror is waiting.</div>
      </div>

      {/* Journey Steps */}
      <div style={{ padding: "0 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.15)" }} />
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: colors.gold }} />
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.15)" }} />
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: "#fff", marginBottom: 10, lineHeight: 1.2, textShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>Your Self-Discovery Journey</h2>
        <p style={{ fontSize: 17, color: "rgba(255,255,255,0.9)", marginBottom: 16, lineHeight: 1.7, fontWeight: 400 }}>
          Start anywhere. Each free preview takes 5 - 10 minutes. Unlock the full 10-page report when you're ready.
        </p>

        {/* Progress bar for returning users */}
        {authUser && (() => {
          const done = journeySteps.filter(s => savedResults?.[`${s.key}Preview`] || savedResults?.[`${s.key}Scores`]).length;
          if (done === 0) return null;
          return (
            <div style={{ marginBottom: 20, padding: "12px 16px", background: "rgba(255,255,255,0.08)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(255,255,255,0.8)", marginBottom: 6 }}>
                <span>🎯 {done} of 5 assessments started</span>
                <span>{paidTests.length} report{paidTests.length !== 1 ? "s" : ""} unlocked</span>
              </div>
              <div style={{ height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${(done / 5) * 100}%`, background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`, borderRadius: 2, transition: "width 0.5s ease" }} />
              </div>
            </div>
          );
        })()}

        {journeySteps.map((step) => {
          const isPaid = paidTests.includes(step.key);
          const hasPreview = savedResults?.[`${step.key}Preview`] || savedResults?.[`${step.key}Scores`];
          return (
            <div key={step.key} style={{ ...cardStyle, marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 32, height: 32, borderRadius: "50%", background: isPaid ? colors.accent : "rgba(30,136,229,0.12)", border: `2px solid ${isPaid ? colors.accent : "rgba(30,136,229,0.3)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: isPaid ? "#fff" : colors.primary, flexShrink: 0, marginTop: 2 }}>
                  {isPaid ? "✓" : step.step}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 18 }}>{step.icon}</span>
                    <h3 style={{ fontSize: 17, fontWeight: 700, color: colors.bgDark, margin: 0 }}>{step.title}</h3>
                  </div>
                  <p style={{ fontSize: 15, color: "#1e2e3c", lineHeight: 1.7, marginBottom: 10, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{step.desc}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 12, padding: "3px 9px", borderRadius: 20, background: "rgba(30,136,229,0.1)", color: "#1255a0", fontWeight: 700 }}>FREE PREVIEW</span>
                    {step.key === "personality" ? (
                      <span style={{ fontSize: 12, padding: "3px 9px", borderRadius: 20, background: "rgba(0,180,100,0.12)", color: "#0a6b3a", fontWeight: 700 }}>✓ ALWAYS FREE</span>
                    ) : (
                      <span style={{ fontSize: 12, padding: "3px 9px", borderRadius: 20, background: isPaid ? "rgba(0,180,100,0.12)" : "rgba(255,150,0,0.1)", color: isPaid ? "#0a6b3a" : "#b36b00", fontWeight: 700 }}>
                        {isPaid ? "✓ FULL REPORT UNLOCKED" : `FULL REPORT ₹${PRICING[step.key].price}`}
                      </span>
                    )}
                    <span style={{ fontSize: 12, padding: "3px 9px", borderRadius: 20, background: "rgba(90,106,122,0.08)", color: "#5a6a7a", fontWeight: 600 }}>{step.questions} questions</span>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                {isPaid ? (
                  savedResults?.[`${step.key}Report`] ? (
                    <button style={{ ...btnPrimary, fontSize: 14 }} onClick={() => {
                      setCurrentTest(step.key);
                      setAiReportFull(savedResults[`${step.key}Report`]);
                      setResults(savedResults[`${step.key}Scores`] || null);
                      setScreen("paid-report");
                    }}>View Full Report →</button>
                  ) : (
                    <button style={{ ...btnPrimary, fontSize: 14 }} onClick={() => startTest(step.key)}>
                      {savedResults?.[`${step.key}Scores`] ? "Generate Full Report →" : "Start Assessment →"}
                    </button>
                  )
                ) : hasPreview ? (
                  <>
                    <button style={{ ...btnSecondary, fontSize: 13, flex: 1 }} onClick={() => {
                      setCurrentTest(step.key);
                      setAiReport(savedResults?.[`${step.key}Preview`] || null);
                      setResults(savedResults?.[`${step.key}Scores`] || null);
                      setScreen("free-results");
                    }}>
                      View Preview
                    </button>
                    {step.key !== "personality" && (
                      <button style={{ ...btnPrimary, fontSize: 13, flex: 1, background: `linear-gradient(135deg, #c8960a, #e0aa10)`, color: "#091a2a" }} onClick={() => { setScreen("purchase"); }}>
                        Unlock ₹{PRICING[step.key].price} →
                      </button>
                    )}
                    {step.key === "personality" && (
                      <button style={{ ...btnPrimary, fontSize: 13, flex: 1 }} onClick={() => startTest("core")}>
                        Explore Your Core →
                      </button>
                    )}
                  </>
                ) : (
                  <button style={{ ...btnPrimary, fontSize: 14 }} onClick={() => startTest(step.key)}>
                    Start Free Assessment →
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bundle */}
      <div style={{ padding: "8px 24px 0" }}>
        <div style={{ ...cardStyle, background: "rgba(12,48,120,0.88)", border: "1px solid rgba(255,215,0,0.25)", color: "#fff", textAlign: "center", backdropFilter: "blur(12px)" }}>
          <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: colors.gold, marginBottom: 8, fontWeight: 700 }}>Best Value</div>
          <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, color: "#fff" }}>Complete Bundle - All 5 Reports</h3>
          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.88)", marginBottom: 16, lineHeight: 1.7 }}>Four deep-dive reports covering your core traits, strengths, shadow side, and values - plus the free Personality assessment.</p>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>
            Individual reports ₹399 - ₹499 = <span style={{ textDecoration: "line-through" }}>₹{PRICING.bundle.original.toLocaleString("en-IN")}</span>
          </div>
          <div style={{ fontSize: 34, fontWeight: 700, color: "#fff", lineHeight: 1 }}>₹{getPrice("bundle")}</div>
          {discountApplied > 0 && <div style={{ fontSize: 13, color: colors.accentLight, fontWeight: 700, marginTop: 2 }}>₹{discountApplied * 2} off with your code!</div>}
          <div style={{ fontSize: 13, color: colors.gold, fontWeight: 700, marginTop: 4, marginBottom: 16 }}>Save ₹{PRICING.bundle.original - getPrice("bundle")} - {Math.round(((PRICING.bundle.original - getPrice("bundle")) / PRICING.bundle.original) * 100)}% off</div>
          <button style={{ ...btnPrimary, background: `linear-gradient(135deg, #c8960a, #e0aa10)`, color: "#091a2a", fontWeight: 700 }} onClick={() => { setScreen("purchase"); }}>
            Unlock Everything - ₹{getPrice("bundle")}
          </button>
        </div>
      </div>

      {/* Discount */}
      <div style={{ padding: "20px 24px 0" }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: colors.bgDark }}>Have a discount code?</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="text" placeholder="Enter code" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
            <button style={{ ...btnPrimary, width: "auto", padding: "10px 20px" }} onClick={() => applyDiscount(discountCode)}>Apply</button>
          </div>
          {discountApplied > 0 && <div style={{ fontSize: 13, color: colors.success, marginTop: 8 }}>✓ ₹{discountApplied} discount active</div>}
        </div>
      </div>

      {/* FAQ */}
      <div style={{ padding: "8px 24px 24px" }}>
        <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,0.55)", textAlign: "center", marginBottom: 14, fontWeight: 700 }}>Common Questions</div>
        {[
          { q: "Is this like MBTI or the 16 personalities test?", a: "Mirror Mirror uses multiple frameworks - MBTI-style personality typing, the scientifically validated Big Five (OCEAN) model, and original assessments for Strengths, Shadow, and Values. It's more comprehensive and research-grounded." },
          { q: "What's the difference between the free preview and full report?", a: "The free preview gives you your scores and a 1-page summary. The full 10-page report goes deep - career paths, relationship patterns, stress signatures, and a personalised action plan." },
          { q: "How is my data used?", a: "Your responses are used only to generate your personal report. We never sell your data. Reports are stored securely and accessible only to you." },
          { q: "Can I retake an assessment?", a: "Yes - you can retake any assessment at any time. Your most recent results are saved to your profile." },
        ].map((faq, i) => (
          <details key={i} style={{ ...cardStyle, marginBottom: 8, cursor: "pointer" }}>
            <summary style={{ fontSize: 15, fontWeight: 600, color: colors.bgDark, listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span>{faq.q}</span><span style={{ color: colors.primary, fontSize: 20, flexShrink: 0 }}>+</span>
            </summary>
            <p style={{ fontSize: 15, color: "#1e2e3c", lineHeight: 1.7, margin: "10px 0 0" }}>{faq.a}</p>
          </details>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: "24px 24px 40px", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: 1.8, fontWeight: 400, marginBottom: 16 }}>
          Mirror Mirror is a self-awareness tool inspired by established psychological frameworks.<br />Not a clinical assessment.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <button onClick={() => setScreen("methodology")} style={{ background: "none", border: "none", fontSize: 14, color: "rgba(255,255,255,0.75)", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
            📖 Our Methodology & Science
          </button>
          <a href={`https://wa.me/${CONFIG.WHATSAPP}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: "#fff", textDecoration: "none", fontWeight: 600 }}>💬 WhatsApp Support →</a>
          <a href="mailto:reports@mirrormirror.life" style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", textDecoration: "none" }}>📧 reports@mirrormirror.life</a>
        </div>
      </div>
    </div>
  );
}
